import React, { useState, useEffect } from "react";
import {
  Box, Typography, Grid, FormControl, InputLabel, Select, MenuItem,
  TextField, Button, Modal, Backdrop, FormControlLabel, Checkbox,
} from "@mui/material";
import axios from "axios";
import { toast } from "react-toastify";
import { useDollar } from "../../../DollarPrice";
import { getAuthData } from "../../utils/auth";
import CloudUploadIcon from '@mui/icons-material/CloudUpload'; // Asegúrate de tener @mui/icons-material

const BANCO_RECEPTOR = "Banco Banesco";
const NUMERO_CUENTA = "0134-0319-82-3191111990";
const TELEFONO_PAGO_MOVIL = "0414-4282678";
const RIF = "J-408124637";
const apiUrl = process.env.REACT_APP_API_URL;

const ReportePagosModal = ({ open, onClose, solicitud, onPagoRegistrado }) => {
  const { dollarPrice } = useDollar();

  const [metodoPago, setMetodoPago] = useState("");
  const [montoBaseUSD, setMontoBaseUSD] = useState(0);
  const [aplicarDescuento, setAplicarDescuento] = useState(false);
  const [pagoData, setPagoData] = useState({
    bancoEmisorVes: "",
    referenciaVes: "",
    comprobanteVes: null,
    observaciones: ""
  });

  const authData = getAuthData();
  const token = authData?.token;
  const vendedor_id = authData?.vendedorId;
  const supervisor_id = authData?.supervisorId;

  const banks = [
    { value: "", label: "Seleccione un Banco" },
    { value: "Banesco", label: "Banco Banesco Banco Universal, C.A." },
    { value: "100% Banco", label: "100% Banco Banco Universal, C.A." },
    { value: "Banca Amiga", label: "Banca Amiga, Banco Microfinanciero" },
    { value: "Bancaribe", label: "Bancaribe, C.A. Banco Universal" },
    { value: "Banco Activo", label: "Banco Activo" },
    { value: "Banco Bicentenario", label: "Banco Bicentenario" },
    { value: "Banco Caroní", label: "Banco Caroní, C.A. Banco Universal" },
    { value: "Banco de la FANB", label: "Banco de la FANB" },
    { value: "Banco de Venezuela", label: "Banco de Venezuela" },
    { value: "Banco del Tesoro", label: "Banco del Tesoro" },
    { value: "Banco Exterior", label: "Banco Exterior, C.A. Banco Universal" },
    { value: "Banco Mercantil", label: "Banco Mercantil" },
    { value: "BNC", label: "Banco Nacional de Crédito (BNC)" },
    { value: "Banco Provincial", label: "Banco Provincial" },
    { value: "Banplus", label: "Banplus Banco Universal, C.A." },
  ];

  useEffect(() => {
    if (open && solicitud) {
      const monto = solicitud.modalidad_pago === "financiado"
        ? Number(solicitud.monto_por_cuota)
        : Number(solicitud.precio_plan);

      setMontoBaseUSD(monto);
      setAplicarDescuento(false);

      const tPlan = solicitud.tipo_plan?.toLowerCase();
      const tServ = solicitud.tipo_servicio?.toLowerCase();
      const mod = solicitud.modalidad_pago?.toLowerCase();

      if (tPlan === "producto" && (tServ === "parcela" || mod === "financiado")) {
        setMetodoPago("USD");
      } else {
        setMetodoPago(solicitud.metodo_pago || "USD");
      }

      setPagoData({ bancoEmisorVes: "", referenciaVes: "", comprobanteVes: null, observaciones: "" });
    }
  }, [open, solicitud]);

  const permiteCambiarMetodo = () => {
    const tPlan = solicitud?.tipo_plan?.toLowerCase();
    const tServ = solicitud?.tipo_servicio?.toLowerCase();
    const mod = solicitud?.modalidad_pago?.toLowerCase();
    if (tPlan?.includes("servicio")) return true;
    if (tPlan === "producto" && tServ === "nicho_columbario" && mod === "contado") return true;
    return false;
  };

  const montoDescuento = aplicarDescuento ? montoBaseUSD * 0.20 : 0;
  const totalEfectivoCobrar = montoBaseUSD - montoDescuento;
  const totalFinalAMostrar = metodoPago === "VES"
    ? (montoBaseUSD * dollarPrice).toFixed(2)
    : totalEfectivoCobrar.toFixed(2);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const loadingToast = toast.loading("Registrando pago...");
    try {
      const formData = new FormData();

      // --- CAMPOS REQUERIDOS SEGÚN EL ERROR ---
      formData.append("solicitud_id", solicitud.id);
      formData.append("cliente_id", solicitud.cliente_id); // Requerido
      formData.append("numero_solicitud", solicitud.numero_solicitud); // Requerido
      formData.append("tipo_servicio", solicitud.tipo_servicio); // Requerido
      formData.append("modalidad_pago", solicitud.modalidad_pago); // Requerido

      // Fecha formateada (YYYY-MM-DD HH:MM:SS) - Requerido
      const fechaActual = new Date().toISOString().slice(0, 19).replace('T', ' ');
      formData.append("fecha_pago", fechaActual);

      // --- CAMPOS DE LÓGICA DE PAGO ---
      formData.append("vendedor_id", vendedor_id);
      formData.append("supervisor_id", supervisor_id);
      formData.append("metodo_pago", metodoPago);
      formData.append("monto_pagado", totalFinalAMostrar); // Lo que paga físicamente
      formData.append("descuento", montoDescuento);
      formData.append("monto_usd", montoBaseUSD.toFixed(2)); // Abono total
      formData.append("dollar_price", dollarPrice);
      formData.append("no_cuotas", solicitud.numero_cuotas || 0);
      formData.append("estado_pago", solicitud.modalidad_pago === "financiado" ? "inicial_pagada" : "pagado");
      formData.append("observaciones", pagoData.observaciones || "");

      // --- DATOS BANCARIOS (Si es VES) ---
      if (metodoPago === "VES") {
        formData.append("banco_emisor", pagoData.bancoEmisorVes);
        formData.append("banco_receptor", BANCO_RECEPTOR);
        formData.append("referencia", pagoData.referenciaVes);
        if (pagoData.comprobanteVes) {
          formData.append("comprobante_ves", pagoData.comprobanteVes);
        }
      }

      const response = await axios.post(`${apiUrl}/api/reporte-pagos/guardar-pago`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });

      toast.update(loadingToast, { render: "Pago registrado exitosamente", type: "success", isLoading: false, autoClose: 3000 });
      onPagoRegistrado(response.data);
      onClose();
    } catch (error) {
      console.error("Error completo:", error.response?.data); // Para que veas en consola si falta algo más
      toast.update(loadingToast, { render: "Error en el servidor. Revisa los datos.", type: "error", isLoading: false, autoClose: 3000 });
    }
  };

  return (
    <Modal open={open} onClose={onClose} closeAfterTransition BackdropComponent={Backdrop}>
      <Box sx={{
        position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)",
        width: "550px", bgcolor: "background.paper", boxShadow: 24, p: 4, borderRadius: 3
      }}>
        <Box sx={{ borderBottom: "2px solid #f0f0f0", pb: 1, mb: 2 }}>
          <Typography variant="h6" sx={{ fontFamily: "Bakbak One", color: "#0A4575" }}>
            REPORTAR PAGO <span style={{ color: "#d32f2f" }}>#{solicitud?.numero_solicitud}</span>
          </Typography>
          <Typography variant="body2" color="textSecondary">
            {solicitud?.tipo_plan?.toUpperCase()} - {solicitud?.tipo_servicio?.replace('_', ' ').toUpperCase()}
          </Typography>
        </Box>

        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6}>
            <Box sx={{ p: 1.5, bgcolor: "#f8f9fa", borderRadius: 2, border: "1px solid #dee2e6", boxShadow: 2 }}>
              <Typography variant="caption" display="block">Inicial a Pagar</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>${montoBaseUSD.toFixed(2)}</Typography>
            </Box>
          </Grid>
          <Grid item xs={6}>
            <Box sx={{ p: 1.5, bgcolor: "#e8f5e9", borderRadius: 2, border: "1px solid #c8e6c9", boxShadow: 2 }}>
              <Typography variant="caption" display="block">Tasa del día (BCV)</Typography>
              <Typography variant="h6" sx={{ fontWeight: 700, color: "#2e7d32" }}>{dollarPrice} Bs.</Typography>
            </Box>
          </Grid>
        </Grid>

        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Forma de Pago</InputLabel>
                <Select
                  value={metodoPago}
                  label="Forma de Pago"
                  onChange={(e) => {
                    setMetodoPago(e.target.value);
                    if (e.target.value === "VES") setAplicarDescuento(false);
                  }}
                  disabled={!permiteCambiarMetodo()}
                  sx={{ boxShadow: 3 }}
                >
                  <MenuItem value="USD">Dólares (Efectivo)</MenuItem>
                  <MenuItem value="VES">Bolívares (Transferencia/Pago Móvil)</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            {metodoPago === "USD" && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={aplicarDescuento}
                      onChange={(e) => setAplicarDescuento(e.target.checked)}
                      color="success"
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 'bold', color: '#2e7d32' }}>Aplicar descuento del 20% por pago en divisas</Typography>}
                />
              </Grid>
            )}

            {metodoPago === "VES" && (
              <>
                <Grid item xs={6}>
                  <FormControl fullWidth size="small" sx={{ boxShadow: 1 }}>
                    <InputLabel>Banco Emisor</InputLabel>
                    <Select
                      value={pagoData.bancoEmisorVes}
                      onChange={(e) => setPagoData({ ...pagoData, bancoEmisorVes: e.target.value })}
                      label="Banco Emisor"
                    >
                      {banks.map((b) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    fullWidth label="Referencia" size="small" required
                    value={pagoData.referenciaVes}
                    onChange={(e) => setPagoData({ ...pagoData, referenciaVes: e.target.value })}
                    sx={{ boxShadow: 3 }}
                  />
                </Grid>

                {/* BOTÓN PARA SUBIR COMPROBANTE */}
                <Grid item xs={12}>
                  <Button
                    variant={pagoData.comprobanteVes ? "contained" : "outlined"}
                    component="label"
                    fullWidth
                    color={pagoData.comprobanteVes ? "success" : "primary"}
                    startIcon={pagoData.comprobanteVes ? null : <CloudUploadIcon />}
                    sx={{ textTransform: 'none', py: 1 }}
                  >
                    {pagoData.comprobanteVes
                      ? `✓ ${pagoData.comprobanteVes.name.substring(0, 20)}...`
                      : "Adjuntar Voucher / Comprobante"}
                    <input
                      type="file"
                      hidden
                      accept="image/*,application/pdf"
                      onChange={(e) => setPagoData({ ...pagoData, comprobanteVes: e.target.files[0] })}
                    />
                  </Button>
                </Grid>

                <Grid item xs={12}>
                  <Box sx={{ p: 1.5, border: "1px dashed #0A4575", borderRadius: 1, bgcolor: "#f0f7ff" }}>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>RECEPTOR: {BANCO_RECEPTOR} | {NUMERO_CUENTA}</Typography>
                    <Typography sx={{ fontSize: '14px', fontWeight: 600 }}>RIF: {RIF} | PAGO MÓVIL: {TELEFONO_PAGO_MOVIL}</Typography>
                  </Box>
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Box sx={{ mt: 1, p: 2, bgcolor: "#f1f8e9", borderRadius: 2, border: "1px solid #dcedc8", boxShadow: 2 }}>

                {/* Fila 1: Abono al contrato */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
                    INICIAL A PAGAR:
                  </Typography>
                  <Typography variant="subtitle2" sx={{ fontWeight: 800, color: "#0A4575" }}>
                    ${montoBaseUSD.toFixed(2)} USD
                  </Typography>
                </Box>

                {/* Fila 2: Descuento (Solo si aplica) */}
                {aplicarDescuento && (
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="caption" sx={{ color: "green", fontWeight: 600 }}>
                      Descuento aplicado (20% en Divisas):
                    </Typography>
                    <Typography variant="caption" sx={{ fontWeight: 700, color: "green" }}>
                      - ${montoDescuento.toFixed(2)}
                    </Typography>
                  </Box>
                )}

                {/* Línea divisoria */}
                <Box sx={{ borderTop: "1px solid #c8e6c9", mt: 1, pt: 1 }} />

                {/* Fila 3: Total Final a Pagar */}
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                  <Typography sx={{ fontWeight: "bold", fontSize: "0.9rem" }}>
                    TOTAL A PAGAR:
                  </Typography>
                  <Typography sx={{ fontWeight: 900, color: "#d32f2f", fontSize: '1.2rem' }}>
                    {totalFinalAMostrar} {metodoPago}
                  </Typography>
                </Box>

              </Box>
            </Grid>
          </Grid>

          <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 4, gap: 2 }}>
            <Button onClick={onClose} variant="text" sx={{ color: "#666" }}>Cancelar</Button>
            <Button
              type="submit"
              variant="contained"
              sx={{ bgcolor: "#0A4575", px: 4, fontWeight: 'bold' }}
              disabled={metodoPago === "VES" && (!pagoData.bancoEmisorVes || !pagoData.referenciaVes)}
            >
              Confirmar Pago
            </Button>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};

export default ReportePagosModal;